import {
  writeFile as XLSXWriteFile,
  utils as XLSXUtils,
  read as XLSXRead
} from 'xlsx/xlsx.mini';
import { v4 as uuidv4 } from 'uuid';
import { Parser } from '@json2csv/plainjs';
import analytics from '@xto10x/core/lib/analytics';
import { AnalyticsCategory } from 'utils/analytics.constants';
import { downloadCSVFile } from '@xto10x/core/lib/app.utils';
import storage from '@xto10x/storage/lib';
import { StorageKey, UserRole, UserSubscriptionTypes } from 'utils/constants';
import userApi from 'services/userApi';
import { AllProducts, AllVisibleRolesSuperSet } from './constants';
import {
  checkIfDateStringIsAValidDate,
  removeObjectProperties,
  isEmailRegex as isEmail,
  titleCase
} from './app.utils';
import {
  inviteUsers,
  updateUserManagerDesignationOrDemographics
} from '../containers/Invite/store/actions';

const csvjson = require('csvjson');

const emptyValues = [
  'na',
  'n.a',
  'n.a.',
  'n/a',
  'nil',
  'null',
  '-',
  '#n/a',
  '#',
  '#div/0',
  '#name?',
  '#null!',
  '#num!',
  '#ref!',
  '#value!',
  '#error!'
];

export const ydm_date_regex = /^(19|20)\d{2}-(0[1-9]|1[0-2])-(0[1-9]|1\d|2\d|3[01])$/;

export const createAttribute = (label, type, acceptedValueType) => ({
  label,
  type,
  acceptedValueType
});

export const AttributeType = {
  MANDATORY: 'mandatory',
  OPTIONAL: 'optional'
};

export const MandatoryAttr = {
  name: createAttribute('Employee Name', AttributeType.MANDATORY, 'STRING'),
  employee_email: createAttribute(
    'Employee Email',
    AttributeType.MANDATORY,
    'STRING'
  ),
  manager_email: createAttribute(
    'Manager Email',
    AttributeType.MANDATORY,
    'STRING'
  )
};

export const Attribute = {
  name: createAttribute('Employee Name', AttributeType.MANDATORY, 'STRING'),
  employee_email: createAttribute(
    'Employee Email',
    AttributeType.MANDATORY,
    'STRING'
  ),
  manager_email: createAttribute(
    'Manager Email',
    AttributeType.MANDATORY,
    'STRING'
  ),
  'active/inactive': createAttribute(
    'Active/Inactive',
    AttributeType.OPTIONAL,
    'STRING'
  ),
  gender: createAttribute('Gender', AttributeType.OPTIONAL, 'STRING'),
  location: createAttribute('Location', AttributeType.OPTIONAL, 'STRING'),
  date_of_joining: createAttribute(
    'Date of Joining',
    AttributeType.OPTIONAL,
    'DATE'
  ),
  date_of_birth: createAttribute('DOB', AttributeType.OPTIONAL, 'DATE'),
  contact_number: createAttribute(
    'Contact Number',
    AttributeType.OPTIONAL,
    'STRING'
  ),
  employee_type: createAttribute(
    'Employee Type',
    AttributeType.OPTIONAL,
    'STRING'
  ),
  department: createAttribute('Department', AttributeType.OPTIONAL, 'STRING'),
  sub_department: createAttribute(
    'Sub Department',
    AttributeType.OPTIONAL,
    'STRING'
  ),
  grade: createAttribute('Grade', AttributeType.OPTIONAL, 'STRING'),
  designation: createAttribute('Designation', AttributeType.OPTIONAL, 'STRING'),
  performance_data: createAttribute(
    'Performance Data',
    AttributeType.OPTIONAL,
    'STRING'
  )
};

export const getHrbpDemographicAttributes = () => ({
  hrbp_emails: createAttribute('hrbp_emails', AttributeType.OPTIONAL, 'STRING')
});

export const getAttributesByType = (attributes, type) => {
  const attributesByType = Object.keys(attributes).filter(
    key => attributes[key].type === type
  );
  return attributesByType;
};

export const getAcceptedHeaders = (mand, optn) => [
  ...mand,
  ...Object.keys(optn)
];

export const InviteUtils = {
  isNullCsvField: val =>
    !val || emptyValues.includes(val?.toString().toLowerCase()),
  validateYmdDate: val => {
    const now = new Date().getTime();
    return (
      val &&
      ydm_date_regex.test(val) &&
      now > new Date(val).getTime() &&
      checkIfDateStringIsAValidDate(val)
    );
  },
  validate: (val, attributeType) => {
    if (!InviteUtils.isNullCsvField(val)) {
      if (attributeType === 'DATE') {
        return InviteUtils.validateYmdDate(val) ? false : 'date';
      }
      return false;
    }
    return 'empty';
  },
  buildDemographicsAttributeFromCSV: (acceptedHeaders, row, attributes) => {
    const demographicsInfo = {};
    const warnings = [];
    const errors = [];
    attributes.forEach(a => {
      if (acceptedHeaders[a]) {
        const validationError = InviteUtils.validate(
          row[a],
          acceptedHeaders[a].acceptedValueType
        );
        if (!validationError) {
          demographicsInfo[a] = row[a];
        } else {
          demographicsInfo[a] = '';
          const warningId = `${
            validationError === 'date'
              ? 'INVALID_DATE_ATTRIBUTE_'
              : 'INVALID_ATTRIBUTE_'
          }${a.toUpperCase()}`;
          if (acceptedHeaders[a]?.type === AttributeType.MANDATORY) {
            errors.push(warningId);
          } else {
            warnings.push(warningId);
          }
        }
      }
    });
    return [demographicsInfo, warnings, errors];
  },
  isEmailContainAllowedDomain: (domains, email) => {
    const emailDomain = email.split('@')[1];
    return domains.some(domain => domain === emailDomain);
  },
  addErrorProp: (user, ...props) => {
    const errors = user.errorProps || [];
    errors.push(...props);
    // eslint-disable-next-line no-param-reassign
    user.errorProps = errors;
    return user;
  },
  getAttributeLabelBasedOnKey: (key, type) => {
    const newKey = key.replaceAll('_', ' ');
    return (
      newKey.charAt(0).toUpperCase() +
      newKey.slice(1) +
      (type === AttributeType.MANDATORY ? '*' : '')
    );
  }
};

export const createItemData = obj => obj;

const DummyCsvData = {
  name: ['Dinesh', 'Palak', 'Amit', 'Robin'],
  employee_email: [
    'dinesh@xyz.com',
    'palak@xyz.com',
    'amit@xyz.com',
    'robin@xyz.com'
  ],
  manager_email: [
    'founder',
    'dinesh@xyz.com',
    'dinesh@xyz.com',
    'palak@xyz.com'
  ],
  location: ['Bangalore', 'Mumbai', 'Mumbai', 'Bangalore'],
  date_of_joining: ['2018-08-07', '2020-09-28', '2020-05-21', '2020-01-12'],
  date_of_birth: ['1994-09-05', '1991-07-04', '1990-03-26', '1988-12-16'],
  employee_type: ['Full time', 'Third Party', 'Full time', 'Full time'],
  gender: ['Male', 'Female', 'Male', 'Male'],
  department: ['Corporate', 'Technology', 'Operations', 'Operations'],
  sub_department: ['Corporate', 'Devops', 'Logistics', 'Logistics'],
  grade: ['G0', 'G0', 'G0', 'G0'],
  performance_rating: [
    'Exceeds expectation',
    'Exceeds expectation',
    'Meets expectation',
    'Below expectation'
  ],
  'active/inactive': ['Active', 'Active', 'Active', 'Inactive'],
  contact_number: ['9111222333', '8111222333', '7111222333', '6111222333'],
  designation: ['Manager', 'Employee', 'Founder', 'CEO'],
  hrbp_emails: [
    'hrbp1@xyz.com,hrbp2@xyz.com',
    'hrbp3@xyz.com',
    'hrbp4@xyz.com',
    'hrbp5@xyz.com'
  ]
};

export const createJsonDownloadTemplate = attributes => {
  const users = [];
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < 4; i++) {
    const u = {
      name: DummyCsvData.name[i],
      employee_email: DummyCsvData.employee_email[i],
      manager_email: DummyCsvData.manager_email[i]
    };
    attributes.forEach(a => {
      u[a] = DummyCsvData[a] ? DummyCsvData[a][i] : `${a}_value_${i + 1}`;
    });
    users.push(u);
  }
  return users;
};

export const downloadExcelFile = (
  json,
  fileName = 'undefined',
  sheetName = 'Sheet 1'
) => {
  const wb = XLSXUtils.book_new();
  const ws = XLSXUtils.json_to_sheet(json);
  XLSXUtils.book_append_sheet(wb, ws, sheetName);
  XLSXWriteFile(wb, `${fileName}.xlsx`);
};

export const ExcelFormats = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  // removing this as we will not be supporting this temporarily due to limitations in xlsx library
  // 'application/vnd.ms-excel'
];

export const CsvFormats = ['text/csv', '.csv'];

export const AllowedFileUploadExtensions = [...ExcelFormats];

export const test = userList => {
  try {
    const filename = '10xGoals_active_users_list.csv';
    const json2csvParser = new Parser({
      fields: [
        { label: 'Name', value: 'name' },
        { label: 'Email', value: 'email' },
        { label: 'Role', value: 'role' },
        { label: 'Status', value: 'status' },
        { label: 'Department', value: 'status' }
      ]
    });
    const users = [];
    for (let i = 0; i < userList.length; i++) {
      const u = {};
      Object.keys(Attribute).forEach(a => {
        u[a] = userList[i][a]
          ? userList[i][a]
          : userList[i]?.user_attributes[a];
      });
      users.push(u);
    }

    const csv = json2csvParser.parse(users);
    downloadCSVFile(csv, filename, 'text/csv');
  } catch (err) {
    analytics.trackError('export_user_list_csv', err);
  } finally {
    analytics.trackEvent(AnalyticsCategory.cta_click, 'export_user_list_csv');
  }
};

const getHrbpsForUser = (user, allHrbpUsers, hrbpsMap) => {
  if (!allHrbpUsers.length) return [];
  if (!Object.keys(hrbpsMap?.hrbp_users_map).length) return [];
  const hrbpIdsForUsers = Object.keys(
    hrbpsMap.hrbp_users_map
  ).filter(referenceId =>
    hrbpsMap.hrbp_users_map[referenceId].includes(user.user_reference_id)
  );
  return allHrbpUsers.filter(hrbp =>
    hrbpIdsForUsers.includes(hrbp.user_reference_id)
  );
};

export const handleExportUserList = (
  list,
  orgDemographicKeys,
  orgAccessibleModules = [],
  allHrbpUsers,
  hrbpsMap
) => {
  const users = [];
  const okrModuleIdentifiers = Object.values(UserSubscriptionTypes).map(_ =>
    _.toLowerCase()
  );
  const isOkrModuleEnabled = orgAccessibleModules.some(
    module => okrModuleIdentifiers.indexOf(module.toLowerCase()) > -1
  );
  for (let i = 0; i < list.length; i++) {
    const u = {
      name: list[i].name,
      employee_email: list[i].email,
      manager_email:
        list[i].designation && list[i].designation === 'FOUNDER'
          ? 'FOUNDER'
          : list[i].manager?.email,
      hrbp_emails: getHrbpsForUser(list[i], allHrbpUsers, hrbpsMap)
        .map(hrbp => hrbp.email)
        .join(',')
    };
    if (isOkrModuleEnabled) {
      const userOkrAccessibleModules =
        list[i].user_product_details_list?.find(
          ({ product_reference_id }) =>
            product_reference_id.toLowerCase() === 'okr'
        )?.accessible_modules || [];
      u.okr_access_type =
        userOkrAccessibleModules?.length > 0
          ? userOkrAccessibleModules?.indexOf(UserSubscriptionTypes.ADVANCED) >
            -1
            ? 'OKR'
            : 'NON-OKR'
          : 'No OKR Access';
    }
    Object.keys(orgDemographicKeys).forEach(a => {
      if (list[i].user_attributes && list[i].user_attributes[a])
        u[a] = list[i]?.user_attributes[a];
    });
    const roles = [];
    list[i].user_product_details_list.forEach(p => {
      if (
        p.product_reference_id === AllProducts.APPS &&
        p.user_role === UserRole.ADMIN
      )
        roles.push(AllVisibleRolesSuperSet.ADMIN);
      if (
        p.product_reference_id === AllProducts.APPS &&
        p.user_role === UserRole.SUPER_ADMIN
      )
        roles.push(AllVisibleRolesSuperSet.SUPER_ADMIN);
      if (
        p.product_reference_id === AllProducts.OKR &&
        p.user_role === UserRole.ADMIN
      )
        roles.push(AllVisibleRolesSuperSet.GOALS_ADMINISTRATOR);
      if (
        p.product_reference_id === AllProducts.OKR &&
        p.user_role === UserRole.ADMIN
      )
        roles.push(AllVisibleRolesSuperSet.PERFORMANCE_ADMINISTRATOR);
      if (
        p.product_reference_id === AllProducts.PEOPLE &&
        p.user_role === UserRole.SUPER_HR
      )
        roles.push(AllVisibleRolesSuperSet.ENGAGEMENT_ADMINISTRATOR);
      if (
        p.product_reference_id === AllProducts.PEOPLE &&
        p.user_role === UserRole.ADMIN
      )
        roles.push(AllVisibleRolesSuperSet.SURVEY_CREATOR);
    });
    u.roles = roles.join(', ');
    users.push(u);
  }
  const orgName = storage.getDataFromLocal(StorageKey.ORG_INFO)?.name;
  downloadExcelFile(users, `${orgName}_users`);
};

export const getRowsFromFile = fileUploadUsersData => {
  let rowsFromFile;
  const options = {
    delimiter: ',', // optional
    quote: '"' // optional
  };
  if (ExcelFormats.includes(fileUploadUsersData.ext)) {
    const workBook = XLSXRead(fileUploadUsersData.content, {
      type: 'binary'
    });
    const workSheetName = workBook.SheetNames[0];
    const workSheet = workBook.Sheets[workSheetName];
    rowsFromFile = XLSXUtils.sheet_to_json(workSheet, {
      raw: false,
      defval: ''
    });
    analytics.trackEvent(
      AnalyticsCategory.cta_click,
      'invite_file_excel',
      fileUploadUsersData.ext
    );
  } else {
    rowsFromFile = csvjson.toObject(fileUploadUsersData.content, options);
    analytics.trackEvent(
      AnalyticsCategory.cta_click,
      'invite_file_csv',
      fileUploadUsersData.ext
    );
  }
  return rowsFromFile;
};

export const createBasicUserObjectWithoutDemographics = u => {
  const employee_email = u.employee_email.trim().toLowerCase();
  const manager_email = u.manager_email?.trim().toLowerCase() || '';
  const secondary_review_approver_email =
    u.secondary_review_approver_email?.trim().toLowerCase() || '';
  const id = uuidv4();
  let hrbp_emails = null;

  if (u.hrbp_emails) {
    hrbp_emails = u.hrbp_emails.split(',').map(e => e.trim().toLowerCase());
  } else if (Object.prototype.hasOwnProperty.call(u, 'hrbp_emails')) {
    hrbp_emails = [];
  }

  return {
    employee_email,
    manager_email,
    secondary_review_approver_email,
    id,
    hrbp_emails
  };
};

export const addDemographicsAndRelatedWarningsToUserObject = (
  u,
  otherAttributeFields,
  supportedCsvHeaderAttributes
) => {
  if (otherAttributeFields.length > 0) {
    const [
      attributes,
      warnings,
      errors
    ] = InviteUtils.buildDemographicsAttributeFromCSV(
      supportedCsvHeaderAttributes,
      u,
      otherAttributeFields
    );
    return {
      attributes,
      warnings: warnings?.length > 0 ? warnings : null,
      error: errors?.length > 0 ? errors[0] : null
    };
  }
};

// This function will check 3 things:
// 1. If the email is valid
// 2. If the email is not already present in the current employee list
// 3. If the email is present in the additional users list
// 4. if the email has a valid domain
// Based on the above 3 conditions, it will return the appropriate error message
export const isEmailValidForAnOrganization = (
  email,
  usersAvailableOnTool,
  allowedDomains,
  additionalUsers = []
) => {
  const errors = {
    isEmailValid: false,
    isEmailAlreadyPresent: false,
    isEmailInAdditionalUsers: false,
    isEmailValidDomain: false
  };
  if (isEmail(email)) {
    errors.isEmailValid = true;
  }
  if (usersAvailableOnTool.has(email)) {
    errors.isEmailAlreadyPresent = true;
  }
  if (!allowedDomains.includes(email.split('@')[1])) {
    errors.isEmailValidDomain = true;
  }
  if (additionalUsers.has(email)) {
    errors.isEmailInAdditionalUsers = true;
  }
  return errors;
};

export const validateUsers = (
  allUsers,
  allEmpMap,
  orgInfo,
  activeUsers,
  pendingUsers
) => {
  const csvLocalSummary = {
    errors: 0,
    warnings: 0,
    update: 0,
    ready: 0,
    disruptiveErrors: 0
  };
  const allAvailableUsers = [...activeUsers, ...pendingUsers].map(em => [
    em.email,
    ''
  ]);
  const csvInviteUsers = [];
  const csvInviteUsersMap = new Map();
  const csvUniqUsers = new Map();
  const currentEmployeeEmailsMap = new Map(allAvailableUsers);
  allUsers.forEach(u => {
    const user = { ...u };
    user.name = user?.attributes?.name;
    delete user?.attributes?.name;
    const {
      manager_email: m_e,
      employee_email: e_e,
      hrbp_emails: hrbp_e
    } = user;

    // check for errors in row
    if (!m_e) {
      // mark empty manager
      InviteUtils.addErrorProp(user, 'employee_email', 'manager_email');
      user.error = 'EMPTY_MANAGER';
    } else if (!((m_e === 'founder' || isEmail(m_e)) && isEmail(e_e))) {
      // mark invalid email
      if (!(m_e === 'founder' || isEmail(m_e))) {
        InviteUtils.addErrorProp(user, 'manager_email');
      }
      if (!isEmail(e_e)) {
        InviteUtils.addErrorProp(user, 'employee_email');
      }
      user.error = 'INVALID_EMAIL';
    } else if (
      orgInfo?.allowed_domains?.length > 0 &&
      (!InviteUtils.isEmailContainAllowedDomain(orgInfo.allowed_domains, e_e) ||
        (!InviteUtils.isEmailContainAllowedDomain(
          orgInfo.allowed_domains,
          m_e
        ) &&
          m_e !== 'founder'))
      // check if allowed domain is not there
    ) {
      // mark invalid domain
      if (
        m_e !== 'founder' &&
        !InviteUtils.isEmailContainAllowedDomain(orgInfo.allowed_domains, m_e)
      ) {
        InviteUtils.addErrorProp(user, 'manager_email');
      }
      if (
        !InviteUtils.isEmailContainAllowedDomain(orgInfo.allowed_domains, e_e)
      ) {
        InviteUtils.addErrorProp(user, 'employee_email');
      }
      user.error = 'INVALID_DOMAIN';
    } else if (m_e === e_e) {
      // mark same email in emp & manager
      InviteUtils.addErrorProp(user, 'employee_email', 'manager_email');
      user.error = 'SAME_EMPLOYEE_MANAGER';
    } else if (e_e && e_e === u.secondary_review_approver_email) {
      InviteUtils.addErrorProp(user, 'secondary_review_approver_email');
      user.error = 'MATRIX_MANAGER_AND_EMPLOYEE_ARE_SAME';
    } else if (m_e && m_e === u.secondary_review_approver_email) {
      InviteUtils.addErrorProp(user, 'secondary_review_approver_email');
      user.error = 'MATRIX_MANAGER_AND_EMPLOYEE_MANAGER_ARE_SAME';
    }

    if (!user.error) {
      if (
        m_e !== 'founder' &&
        !currentEmployeeEmailsMap.has(m_e) &&
        !allEmpMap.has(m_e)
      ) {
        // mark manager email not on tool error
        InviteUtils.addErrorProp(user, 'manager_email');
        user.error = 'MANAGER_NOT_FOUND';
        csvLocalSummary.disruptiveErrors++;
      } else if (allEmpMap.get(e_e)?.length > 1) {
        user.error = 'DUPLICATE_EMPLOYEE_DIFFERENT_MANAGER';
        InviteUtils.addErrorProp(user, 'employee_email');
        // Based on isSameMgr, check following:
        // mark different manager email for same employee : ERROR
        // mark same manager email for same employee : WARNING
      }

      if (hrbp_e?.length > 0) {
        hrbp_e.forEach(hrbpEmail => {
          if (
            !isEmail(hrbpEmail) ||
            (orgInfo?.allowed_domains?.length > 0 &&
              !InviteUtils.isEmailContainAllowedDomain(
                orgInfo.allowed_domains,
                hrbpEmail
              ))
          ) {
            InviteUtils.addErrorProp(user, 'hrbp_emails');
            user.error = 'INVALID_HRBP_EMAIL';
          }
          if (
            !currentEmployeeEmailsMap.has(hrbpEmail) &&
            !allEmpMap.has(hrbpEmail)
          ) {
            InviteUtils.addErrorProp(user, 'hrbp_emails');
            user.error = 'HRBP_NOT_FOUND';
          }
        });
      }

      if (!user.error) {
        if (currentEmployeeEmailsMap.has(e_e)) {
          // mark user as update : UPDATE
          user.update = 'UPDATE_USER';
        } else {
          user.ready = 'READY_USER';
        }
      }
    }

    if (user.error) {
      if (
        user.error === 'INVALID_EMAIL' &&
        !isEmail(m_e) &&
        m_e !== 'founder'
      ) {
        csvLocalSummary.disruptiveErrors++;
      } else if (user.error === 'EMPTY_MANAGER') {
        csvLocalSummary.disruptiveErrors++;
      } else if (user.error === 'INVALID_DOMAIN') {
        if (
          !InviteUtils.isEmailContainAllowedDomain(
            orgInfo.allowed_domains,
            m_e
          ) &&
          m_e !== 'founder'
        )
          csvLocalSummary.disruptiveErrors++;
      }
      csvLocalSummary.errors++;
      if (csvLocalSummary[user.error]) {
        csvLocalSummary[user.error].push(user);
      } else {
        csvLocalSummary[user.error] = [user];
      }
    } else {
      if (user.warning) {
        csvLocalSummary.warnings++;
        if (csvLocalSummary[user.warning]) {
          csvLocalSummary[user.warning].push(user);
        } else {
          csvLocalSummary[user.warning] = [user];
        }
      } else if (user.warnings?.length > 0) {
        csvLocalSummary.warnings += user.warnings.length;
        user.warnings.forEach(w => {
          if (csvLocalSummary[w]) {
            csvLocalSummary[w].push(user);
          } else {
            csvLocalSummary[w] = [user];
          }
        });
      }
      if (user.ready) {
        csvLocalSummary.ready++;
      } else if (user.update) {
        csvLocalSummary.update++;
      }
      removeObjectProperties(user, ['warning', 'update', 'ready']);

      csvInviteUsersMap.set(user.employee_email, {
        email: user.employee_email.toLowerCase(),
        name: user.name,
        user_attributes: user.attributes || {}
      });

      if (user.manager_email.toUpperCase() !== 'FOUNDER') {
        csvInviteUsersMap.set(user.manager_email, {
          ...csvInviteUsersMap.get(user.manager_email),
          email: user.manager_email.toLowerCase(),
          user_attributes:
            csvInviteUsersMap.get(user.manager_email)?.user_attributes || {}
        });
      }

      csvInviteUsers.push({
        email: user.employee_email.toLowerCase(),
        name: user.name,
        user_attributes: user.attributes || {},
        hrbp_emails: user.hrbp_emails,
        manager_email: user.manager_email.toLowerCase()
      });
      csvUniqUsers.set(user.employee_email, {
        employee_email: user.employee_email.toLowerCase(),
        manager_email: user.manager_email.toLowerCase()
      });
    }
    return user;
  });
  return {
    csvLocalSummary,
    csvInviteUsers,
    csvInviteUsersMap,
    csvUniqUsers
  };

  // Update state for each error and warning and prepare summaary
  // prepare final email invite list
};

export const createHrbpUpdateRequestBody = (
  existingHrbpIds,
  selectedHrbpIds
) => {
  const hrbpStatusList = [];
  const removingHrbpIds = [];
  existingHrbpIds.forEach(existingHrbpId => {
    if (selectedHrbpIds.findIndex(userId => userId === existingHrbpId) === -1) {
      hrbpStatusList.push({
        hrbp_user_reference_id: existingHrbpId,
        active: false
      });
      removingHrbpIds.push(existingHrbpId);
    }
  });

  Object.values(selectedHrbpIds).forEach(userId => {
    if (
      existingHrbpIds.findIndex(existingHrbpId => existingHrbpId === userId) ===
      -1
    ) {
      hrbpStatusList.push({
        hrbp_user_reference_id: userId,
        active: true
      });
    }
  });

  return {
    hrbp_status_list: hrbpStatusList
  };
};

export const sendInvitation = (
  userAttributes,
  callback,
  resetForm,
  inviteModalCallback,
  dispatch,
  isEmailToggleChecked,
  userHasAdminAccess
) => {
  const { department, sub_department } = userAttributes.user_attributes;
  const formattedDepartment = titleCase(department);
  const formattedSubdepartment = titleCase(sub_department);
  const formattedUserAttributes = {
    ...userAttributes.user_attributes,
    department: formattedDepartment,
    sub_department: formattedSubdepartment
  };
  let formattedEmployee;
  if (userHasAdminAccess) {
    formattedEmployee = {
      email: userAttributes.employeeEmail.trim().toLowerCase(),
      name: userAttributes.employeeName.trim(),
      user_attributes: formattedUserAttributes,
      manager_email: userAttributes.managerEmail.trim().toLowerCase(),
      hrbp_emails: userAttributes.hrbpEmails.map(hrbp => hrbp.email)
    };
  } else {
    formattedEmployee = {
      email: userAttributes.employeeEmail.trim().toLowerCase(),
      name: userAttributes.employeeName.trim()
    };
  }
  const functionCallback = data => {
    resetForm();
    callback();
    if (inviteModalCallback) inviteModalCallback(data);
  };
  dispatch(
    inviteUsers([formattedEmployee], functionCallback, isEmailToggleChecked)
  );
};

const checkForUserUpdateApartFromNameAndSendUpdateRequest = (
  originalUserObject,
  userAttributes,
  allUsers,
  callback,
  setMemberToBeEdited,
  dispatch
) => {
  const overallFunctionCallback = () => {
    callback();
    setMemberToBeEdited(null);
  };
  dispatch(
    updateUserManagerDesignationOrDemographics(
      userAttributes,
      originalUserObject,
      allUsers,
      overallFunctionCallback
    )
  );
};

export const handleSendInvitationButtonClick = (
  isEditView,
  allUsers,
  userAttributes,
  setMemberToBeEdited,
  originalUserObject,
  resetForm,
  inviteModalCallback,
  isEmailToggleChecked,
  callback,
  userHasAdminAccess,
  dispatch
) => {
  if (isEditView) {
    const originalUser = allUsers.filter(
      user => user.email === userAttributes.employeeEmail
    )[0];

    if (userAttributes.employeeName !== originalUser.name) {
      userApi
        .updateUser({
          user_reference_id: originalUser.user_reference_id,
          name: userAttributes.employeeName
        })
        .then(() => {
          checkForUserUpdateApartFromNameAndSendUpdateRequest(
            originalUserObject,
            userAttributes,
            allUsers,
            callback,
            setMemberToBeEdited,
            dispatch
          );
        });
    } else {
      checkForUserUpdateApartFromNameAndSendUpdateRequest(
        originalUserObject,
        userAttributes,
        allUsers,
        callback,
        setMemberToBeEdited,
        dispatch
      );
    }
  } else {
    sendInvitation(
      userAttributes,
      callback,
      resetForm,
      inviteModalCallback,
      dispatch,
      isEmailToggleChecked,
      userHasAdminAccess
    );
  }
};
